import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Radio, RefreshCw, Settings2, Plus, X as XIcon } from 'lucide-react';
import { supabase } from '@/lib/supabase';
import { isSuperAdmin as isSuperAdminRole } from '@/lib/roles';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { MentionCard, type Mention } from '@/components/MentionCard';
import { useAppSetting, useUpdateAppSetting } from '@/hooks/useAppSettings';
import { writeAuditLog } from '@/lib/audit';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import { formatDistanceToNow } from 'date-fns';

type StatusFilter = 'all' | 'new' | 'flagged';
type SourceFilter = 'all' | 'reddit' | 'web';

// ── Keyword management dialog ─────────────────────────────────────────────────

function KeywordDialog({
  open,
  onClose,
  keywords,
  onSave,
  saving,
}: {
  open: boolean;
  onClose: () => void;
  keywords: string[];
  onSave: (kws: string[]) => void;
  saving: boolean;
}) {
  const [draft, setDraft] = useState<string[]>(keywords);
  const [input, setInput] = useState('');

  function add() {
    const kw = input.trim().toLowerCase();
    if (!kw || draft.includes(kw)) return;
    setDraft(prev => [...prev, kw]);
    setInput('');
  }

  function remove(kw: string) {
    setDraft(prev => prev.filter(k => k !== kw));
  }

  return (
    <Dialog open={open} onOpenChange={v => !v && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Manage Keywords</DialogTitle>
        </DialogHeader>

        <div className="space-y-3">
          <div className="flex gap-2">
            <Input
              placeholder="Add keyword…"
              value={input}
              onChange={e => setInput(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && (e.preventDefault(), add())}
              className="flex-1"
            />
            <Button type="button" onClick={add} disabled={!input.trim()}>
              <Plus className="h-4 w-4" />
            </Button>
          </div>

          <div className="flex flex-wrap gap-2 min-h-[40px]">
            {draft.map(kw => (
              <Badge
                key={kw}
                className="bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400 hover:bg-purple-100 pl-2 pr-1 py-1 gap-1 cursor-default"
              >
                {kw}
                <button
                  onClick={() => remove(kw)}
                  className="ml-0.5 rounded hover:bg-purple-200 dark:hover:bg-purple-800 p-0.5"
                >
                  <XIcon className="h-3 w-3" />
                </button>
              </Badge>
            ))}
            {draft.length === 0 && (
              <p className="text-sm text-muted-foreground">No keywords yet.</p>
            )}
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={() => onSave(draft)} disabled={saving}>
            {saving ? 'Saving…' : 'Save'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────

interface MentionsPageProps {
  userRole: string | null;
}

export function MentionsPage({ userRole }: MentionsPageProps) {
  const queryClient = useQueryClient();
  const isSuperAdmin = isSuperAdminRole(userRole);

  const [statusFilter, setStatusFilter] = useState<StatusFilter>('new');
  const [sourceFilter, setSourceFilter] = useState<SourceFilter>('all');
  const [showLowRelevance, setShowLowRelevance] = useState(false);
  const [showOlder, setShowOlder] = useState(false);
  const [keywordDialogOpen, setKeywordDialogOpen] = useState(false);
  const [refreshing, setRefreshing] = useState(false);

  // Keywords from app_settings
  const { data: keywordsRaw } = useAppSetting('social_listening_keywords');
  const keywords: string[] = Array.isArray(keywordsRaw) ? keywordsRaw : [];
  const updateSetting = useUpdateAppSetting();

  // Fetch mentions
  const { data: mentions = [], dataUpdatedAt, isLoading } = useQuery({
    queryKey: ['mentions', statusFilter, sourceFilter],
    queryFn: async () => {
      let query = supabase
        .from('mentions')
        .select('*')
        .order('published_at', { ascending: false, nullsFirst: false });

      if (statusFilter === 'new') {
        query = query.eq('status', 'new');
      } else if (statusFilter === 'flagged') {
        query = query.eq('status', 'flagged');
      } else {
        query = query.neq('status', 'dismissed');
      }

      if (sourceFilter !== 'all') {
        query = query.eq('source', sourceFilter);
      }

      const { data, error } = await query;
      if (error) throw error;
      return (data ?? []) as Mention[];
    },
    staleTime: 2 * 60 * 1000,
  });

  // Apply relevance + age filters client-side
  // Reddit: 40+ (subreddit context helps); Web: 60+ (needs Malta + PC/brand signal both)
  const sevenDaysAgo = new Date(Date.now() - 7 * 86400_000).toISOString();
  // mentions older than 7d but within the 14d fetch window
  const isOlder = (m: Mention) => !!m.published_at && m.published_at < sevenDaysAgo;
  const isLowRelevance = (m: Mention) =>
    m.source === 'web' ? m.relevance_score < 60 : m.relevance_score < 40;

  const relevant = mentions.filter(m => !isLowRelevance(m));
  const olderCount = relevant.filter(isOlder).length;
  const lowRelevanceCount = mentions.filter(isLowRelevance).length;
  const visible = [
    ...relevant.filter(m => showOlder || !isOlder(m)),
    ...(showLowRelevance ? mentions.filter(isLowRelevance) : []),
  ];

  // Flag / unflag
  const flagMutation = useMutation({
    mutationFn: async (mention: Mention) => {
      const newStatus = mention.status === 'flagged' ? 'new' : 'flagged';
      const { error } = await supabase
        .from('mentions')
        .update({ status: newStatus, actioned_by: (await supabase.auth.getUser()).data.user?.id, actioned_at: new Date().toISOString() })
        .eq('id', mention.id);
      if (error) throw error;
      await writeAuditLog({
        action: newStatus === 'flagged' ? 'flag_mention' : 'unflag_mention',
        entity_type: 'mention',
        entity_id: mention.id,
        entity_label: mention.title ?? mention.url,
      });
    },
    onMutate: async (mention) => {
      await queryClient.cancelQueries({ queryKey: ['mentions'] });
      queryClient.setQueryData(['mentions', statusFilter, sourceFilter], (old: Mention[] = []) =>
        old.map(m => m.id === mention.id
          ? { ...m, status: m.status === 'flagged' ? 'new' : 'flagged' as Mention['status'] }
          : m
        )
      );
    },
    onError: () => toast.error('Failed to update mention'),
    onSettled: () => queryClient.invalidateQueries({ queryKey: ['mentions'] }),
  });

  // Dismiss
  const dismissMutation = useMutation({
    mutationFn: async (mention: Mention) => {
      const { error } = await supabase
        .from('mentions')
        .update({ status: 'dismissed', actioned_by: (await supabase.auth.getUser()).data.user?.id, actioned_at: new Date().toISOString() })
        .eq('id', mention.id);
      if (error) throw error;
      await writeAuditLog({
        action: 'dismiss_mention',
        entity_type: 'mention',
        entity_id: mention.id,
        entity_label: mention.title ?? mention.url,
      });
    },
    onMutate: async (mention) => {
      await queryClient.cancelQueries({ queryKey: ['mentions'] });
      queryClient.setQueryData(['mentions', statusFilter, sourceFilter], (old: Mention[] = []) =>
        old.filter(m => m.id !== mention.id)
      );
    },
    onError: () => toast.error('Failed to dismiss mention'),
    onSettled: () => queryClient.invalidateQueries({ queryKey: ['mentions'] }),
  });

  // Manual refresh — triggers background fetch, auto-refreshes list after ~35s
  async function handleRefresh() {
    setRefreshing(true);
    try {
      const { error } = await supabase.functions.invoke('fetch-mentions');
      if (error) throw error;
      toast.info('Fetching mentions in the background — results will appear shortly…');
      // Auto-refresh the list after the background job has had time to finish
      setTimeout(() => {
        queryClient.invalidateQueries({ queryKey: ['mentions'] });
        setRefreshing(false);
      }, 35_000);
    } catch (err: any) {
      toast.error(`Refresh failed: ${err.message}`);
      setRefreshing(false);
    }
  }

  // Save keywords
  function handleSaveKeywords(kws: string[]) {
    updateSetting.mutate(
      { key: 'social_listening_keywords', value: kws },
      {
        onSuccess: () => {
          toast.success('Keywords saved');
          setKeywordDialogOpen(false);
        },
        onError: () => toast.error('Failed to save keywords'),
      }
    );
  }

  const lastFetched = dataUpdatedAt ? formatDistanceToNow(new Date(dataUpdatedAt), { addSuffix: true }) : null;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="flex items-center gap-2">
            <Radio className="h-5 w-5 text-primary" />
            <h1 className="text-2xl font-bold">Social Mentions</h1>
          </div>
          {lastFetched && (
            <p className="text-sm text-muted-foreground mt-0.5">Last updated {lastFetched}</p>
          )}
        </div>

        <div className="flex gap-2 flex-wrap">
          {isSuperAdmin && (
            <Button variant="outline" size="sm" onClick={() => setKeywordDialogOpen(true)}>
              <Settings2 className="h-4 w-4 mr-1.5" />
              Keywords
            </Button>
          )}
          <Button size="sm" onClick={handleRefresh} disabled={refreshing}>
            <RefreshCw className={cn('h-4 w-4 mr-1.5', refreshing && 'animate-spin')} />
            {refreshing ? 'Fetching…' : 'Refresh'}
          </Button>
        </div>
      </div>

      {/* Filters */}
      <div className="flex items-center gap-3 flex-wrap">
        {/* Status tabs */}
        <div className="flex rounded-lg border overflow-hidden text-sm">
          {(['new', 'flagged', 'all'] as StatusFilter[]).map(f => (
            <button
              key={f}
              onClick={() => setStatusFilter(f)}
              className={cn(
                'px-3 py-1.5 capitalize transition-colors',
                statusFilter === f
                  ? 'bg-primary text-primary-foreground'
                  : 'hover:bg-gray-100 dark:hover:bg-gray-800',
              )}
            >
              {f}
            </button>
          ))}
        </div>

        {/* Source filter */}
        <div className="flex rounded-lg border overflow-hidden text-sm">
          {(['all', 'reddit', 'web'] as SourceFilter[]).map(f => (
            <button
              key={f}
              onClick={() => setSourceFilter(f)}
              className={cn(
                'px-3 py-1.5 capitalize transition-colors',
                sourceFilter === f
                  ? 'bg-primary text-primary-foreground'
                  : 'hover:bg-gray-100 dark:hover:bg-gray-800',
              )}
            >
              {f}
            </button>
          ))}
        </div>

        {/* Older posts toggle */}
        {olderCount > 0 && (
          <button
            onClick={() => setShowOlder(v => !v)}
            className="text-sm text-muted-foreground hover:text-foreground transition-colors underline underline-offset-2"
          >
            {showOlder
              ? `Hide ${olderCount} older`
              : `Show ${olderCount} older (7–14d)`}
          </button>
        )}

        {/* Low relevance toggle */}
        {lowRelevanceCount > 0 && (
          <button
            onClick={() => setShowLowRelevance(v => !v)}
            className="text-sm text-muted-foreground hover:text-foreground transition-colors underline underline-offset-2"
          >
            {showLowRelevance
              ? `Hide ${lowRelevanceCount} low-relevance`
              : `Show ${lowRelevanceCount} low-relevance`}
          </button>
        )}
      </div>

      {/* Results */}
      {isLoading ? (
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {[1, 2, 3, 4, 5, 6].map(i => (
            <div key={i} className="h-40 rounded-xl bg-gray-100 dark:bg-gray-800 animate-pulse" />
          ))}
        </div>
      ) : visible.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-20 text-center gap-3">
          <Radio className="h-10 w-10 text-muted-foreground/30" />
          <p className="text-muted-foreground">
            {mentions.length === 0
              ? 'No mentions yet — hit Refresh to fetch the latest.'
              : 'No mentions match the current filters.'}
          </p>
        </div>
      ) : (
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {visible.map(mention => (
            <MentionCard
              key={mention.id}
              mention={mention}
              onFlag={() => flagMutation.mutate(mention)}
              onDismiss={() => dismissMutation.mutate(mention)}
            />
          ))}
        </div>
      )}

      {/* Keyword dialog */}
      <KeywordDialog
        open={keywordDialogOpen}
        onClose={() => setKeywordDialogOpen(false)}
        keywords={keywords}
        onSave={handleSaveKeywords}
        saving={updateSetting.isPending}
      />
    </div>
  );
}
